import { useSyncExternalStore } from 'react';

/**
 * Glyph ids shown while a picker is hovered. Only icon components subscribe,
 * so a hover does not replace the theme the rest of the viewer reads.
 * @type {Record<string, string>|null}
 */
let glyphs = null;
const listeners = new Set();

function emit() {
    listeners.forEach((listener) => listener());
}

function sameMap(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => a[key] === b[key]);
}

export function subscribeHoverGlyphs(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function hoverGlyphSnapshot() {
    return glyphs;
}

/** @param {Record<string, string>|null} next */
export function setHoverGlyphs(next) {
    const value = next && Object.keys(next).length ? next : null;
    if (sameMap(glyphs, value)) return;
    glyphs = value;
    emit();
}

/**
 * Glyph id for one module while hovering, or undefined when the draft glyph shows.
 * @param {string} moduleId
 * @returns {string|undefined}
 */
export function useHoverGlyph(moduleId) {
    const map = useSyncExternalStore(subscribeHoverGlyphs, hoverGlyphSnapshot, () => null);
    if (!map || !Object.prototype.hasOwnProperty.call(map, moduleId)) return undefined;
    return map[moduleId];
}

/**
 * Catalog pushed with the theme draft. The page's saved library is the fallback
 * for a normal visit, where that draft catalog was never set.
 * @param {Object} [content]
 */
export function previewLibrary(content) {
    if (typeof window !== 'undefined' && window.__LIBRARY__) return window.__LIBRARY__;
    return content?.library || null;
}

function glyphAt(modules, id, layer) {
    const entry = modules?.[id];
    if (!entry) return undefined;
    const box = entry[layer] || entry.desktop;
    if (!box || !Object.prototype.hasOwnProperty.call(box, 'glyph')) return undefined;
    return box.glyph || '';
}

/**
 * Glyphs that differ from the last committed theme. Null when nothing changed,
 * so a font-only hover does not touch the icon store.
 * @param {Object|null} prev
 * @param {Object|null} next
 * @param {string} layer
 * @returns {Record<string, string>|null}
 */
export function hoverGlyphMap(prev, next, layer) {
    if (!prev) return null;
    const bp = layer === 'phone' || layer === 'tablet' ? layer : 'desktop';
    const ids = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
    const map = {};
    ids.forEach((id) => {
        const before = glyphAt(prev, id, bp);
        const after = glyphAt(next, id, bp);
        if (before === after) return;
        map[id] = after == null ? '' : after;
    });
    return Object.keys(map).length ? map : null;
}
